import { Objective, ObjectiveOwner, ObjectiveTask } from '../types/objective.type';
import { Task, TaskApiStatus, WorkStatus } from '../types/task.type';
import { normalizeThreadMessages } from './taskApi.utils';
import { WORK_STATUSES } from './task.utils';

export type ObjectiveDueDateFilter = '' | 'overdue' | 'thisWeek' | 'thisMonth' | 'noDue';

export function objectiveTaskKey(ownerId: string, taskListId: string, googleTaskId: string) {
  return `${ownerId}:${taskListId}:${googleTaskId}`;
}

export function formatObjectiveAmount(value: number | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function ownerFilterKey(owner: ObjectiveOwner) {
  return `${owner.type}:${owner.id}`;
}

function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function isObjectiveOverdue(dueDate: string | null | undefined): boolean {
  if (!dueDate) return false;
  return new Date(dueDate) < startOfToday();
}

function isDueThisWeek(dueDate: string): boolean {
  const d = new Date(dueDate);
  const start = startOfToday();
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return d >= start && d < end;
}

function isDueThisMonth(dueDate: string): boolean {
  const d = new Date(dueDate);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export function getUniqueObjectiveOwners(objectives: Objective[]): ObjectiveOwner[] {
  const map = new Map<string, ObjectiveOwner>();
  for (const objective of objectives) {
    for (const owner of objective.owners ?? []) {
      map.set(ownerFilterKey(owner), owner);
    }
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export interface ObjectiveFilters {
  search?: string;
  teamId?: string;
  ownerKey?: string;
  dueDateFilter?: ObjectiveDueDateFilter;
}

export function filterObjectives(objectives: Objective[], filters: ObjectiveFilters): Objective[] {
  const q = filters.search?.trim().toLowerCase() ?? '';
  return objectives.filter(objective => {
    if (filters.teamId && objective.team_id !== filters.teamId) {
      return false;
    }

    if (filters.ownerKey) {
      const hasOwner = (objective.owners ?? []).some(o => ownerFilterKey(o) === filters.ownerKey);
      if (!hasOwner) return false;
    }

    if (filters.dueDateFilter) {
      switch (filters.dueDateFilter) {
        case 'overdue':
          if (!isObjectiveOverdue(objective.due_date)) return false;
          break;
        case 'thisWeek':
          if (!objective.due_date || !isDueThisWeek(objective.due_date)) return false;
          break;
        case 'thisMonth':
          if (!objective.due_date || !isDueThisMonth(objective.due_date)) return false;
          break;
        case 'noDue':
          if (objective.due_date) return false;
          break;
        default:
          break;
      }
    }

    if (!q) return true;
    return (
      objective.title.toLowerCase().includes(q) ||
      (objective.team_name ?? '').toLowerCase().includes(q) ||
      (objective.metric ?? '').toLowerCase().includes(q) ||
      (objective.owners ?? []).some(o => o.name.toLowerCase().includes(q))
    );
  });
}

export function objectiveTaskToTask(
  linked: ObjectiveTask,
  nameByOwnerId?: Map<string, string>,
): Task {
  const status: TaskApiStatus = linked.status === 'completed' || linked.work_status === 'completed'
    ? 'completed'
    : 'needsAction';
  const workStatus: WorkStatus = linked.work_status && WORK_STATUSES.includes(linked.work_status)
    ? linked.work_status
    : (status === 'completed' ? 'completed' : 'not_started');
  const employeeName =
    linked.employee_name
    ?? linked.owner_name
    ?? nameByOwnerId?.get(linked.owner_id)
    ?? '—';
  return {
    google_task_id: linked.google_task_id,
    title: linked.task_title,
    notes: linked.notes ?? null,
    status,
    work_status: workStatus,
    linked_objectives: [],
    due: linked.due ?? null,
    is_overdue: linked.is_overdue,
    updated: linked.updated ?? new Date().toISOString(),
    employee_id: linked.owner_id,
    employee_name: employeeName,
    task_list_id: linked.task_list_id,
    task_list_name: linked.task_list_name ?? '',
    space_name: linked.space_name ?? null,
    thread_link: linked.thread_link ?? null,
    thread_name: linked.thread_name ?? null,
    thread_messages: normalizeThreadMessages(linked.thread_messages),
  };
}
